import { useCallback, useEffect, useRef, useState } from 'react';
import { retrieveAuthTokens } from '../utils/next/auth.utils';

export type LiveTranscriptionErrorKind =
  | 'unsupported'
  | 'permission'
  | 'connection';

export interface UseLiveTranscriptionOptions {
  /** Full running transcript (committed segments + in-progress text) on each change. */
  readonly onTranscript: (text: string) => void;
  /** Called when capture/stream can't start or fails; the session is torn down. */
  readonly onError?: (kind: LiveTranscriptionErrorKind, message: string) => void;
}

export interface LiveTranscriptionControls {
  /** True from start() until torn down — connecting or actively streaming. */
  readonly isActive: boolean;
  /** True once the server is ready and mic audio is streaming. */
  readonly isRecording: boolean;
  readonly start: () => Promise<void>;
  readonly stop: () => void;
}

const TARGET_SAMPLE_RATE = 24000;
// Must match `registerProcessor(...)` in the worklet asset below.
const PROCESSOR_NAME = 'cowbell-pcm-downsampler';
// Static same-origin worklet asset (apps/console/public/js/workers/...), so it
// loads under CSP `script-src 'self'` without a blob: URL.
const WORKLET_MODULE_URL = '/js/workers/pcm-downsampler.worklet.js';
// App-level WS subprotocol the server echoes back on a successful handshake.
// The JWT rides alongside it as a second `bearer.<jwt>` subprotocol entry.
const WS_APP_PROTOCOL = 'chat-agent-transcribe.v1';
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

function getAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') {
    return AudioContext;
  }
  return (globalThis as unknown as { webkitAudioContext?: typeof AudioContext })
    .webkitAudioContext;
}

interface ControlMessage {
  type: string;
  text?: unknown;
  error?: unknown;
}

/** Parses a server text frame into a control message, or null if malformed. */
function parseControlMessage(raw: string): ControlMessage | null {
  let msg: ControlMessage;
  try {
    msg = JSON.parse(raw) as ControlMessage;
  } catch {
    return null;
  }
  if (!msg || typeof msg.type !== 'string') {
    return null;
  }
  return msg;
}

interface SessionError {
  kind: LiveTranscriptionErrorKind;
  message: string;
}

/** Browser-support and secure-context preconditions, checked before connecting. */
function getEnvironmentError(): SessionError | null {
  if (
    !navigator.mediaDevices?.getUserMedia ||
    !getAudioContextCtor() ||
    typeof AudioWorkletNode === 'undefined'
  ) {
    return {
      kind: 'unsupported',
      message: 'Your browser does not support live voice input.',
    };
  }
  // Never send the token over an unencrypted ws:// except on localhost (dev);
  // in any real deployment the page is https, so the socket is wss.
  const { protocol, hostname } = globalThis.location;
  if (protocol !== 'https:' && !LOCAL_HOSTS.includes(hostname)) {
    return {
      kind: 'connection',
      message: 'Voice input requires a secure (HTTPS) connection.',
    };
  }
  return null;
}

/**
 * Streams microphone audio to the live transcription WebSocket and surfaces the
 * running transcript as it arrives.
 *
 * Pipeline: getUserMedia → AudioWorklet (resample to 24 kHz mono Int16 PCM) →
 * binary WS frames. The socket authenticates via the `bearer.<jwt>` WS
 * subprotocol (keeps the token out of URLs/logs); audio is only sent after the
 * server's `{"type":"ready"}`. Inbound `delta` appends to the in-progress
 * segment, `final` commits it, and `error` tears the session down.
 */
export function useLiveTranscription({
  onTranscript,
  onError,
}: UseLiveTranscriptionOptions): LiveTranscriptionControls {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const wsRef = useRef<WebSocket | null>(null);
  const readyRef = useRef(false);
  // True once the mic audio graph is live and streaming frames.
  const audioLiveRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isMountedRef = useRef(true);
  // Fails the session if the server never sends `{"type":"ready"}`.
  const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Transcript assembly: committed (finalized) segments + the in-progress one.
  const committedRef = useRef('');
  const partialRef = useRef('');

  // Flip to "recording" only once the server is ready AND mic audio is actually
  // streaming — not on `ready` alone (mic permission may still be pending).
  const markRecordingIfReady = useCallback(() => {
    if (readyRef.current && audioLiveRef.current && isMountedRef.current) {
      setIsConnecting(false);
      setIsRecording(true);
    }
  }, []);

  const emitTranscript = useCallback(() => {
    const committed = committedRef.current;
    const partial = partialRef.current;
    const text =
      committed && partial ? `${committed} ${partial}` : committed || partial;
    onTranscriptRef.current(text);
  }, []);

  const teardown = useCallback(() => {
    readyRef.current = false;
    audioLiveRef.current = false;
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      // Detach handlers first so the close below isn't reported as an error.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      }
    }
  }, []);

  const fail = useCallback(
    (kind: LiveTranscriptionErrorKind, message: string) => {
      teardown();
      if (isMountedRef.current) {
        setIsRecording(false);
        setIsConnecting(false);
      }
      onErrorRef.current?.(kind, message);
    },
    [teardown]
  );

  const commitFinalSegment = useCallback(
    (text: unknown) => {
      const segment = typeof text === 'string' ? text : partialRef.current;
      const trimmed = segment.trim();
      if (trimmed) {
        committedRef.current = committedRef.current
          ? `${committedRef.current} ${trimmed}`
          : trimmed;
      }
      partialRef.current = '';
      emitTranscript();
    },
    [emitTranscript]
  );

  const handleReady = useCallback(() => {
    readyRef.current = true;
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
    markRecordingIfReady();
  }, [markRecordingIfReady]);

  const handleMessage = useCallback(
    (raw: string) => {
      const msg = parseControlMessage(raw);
      if (!msg) {
        return;
      }
      switch (msg.type) {
        case 'ready':
          handleReady();
          break;
        case 'delta':
          if (typeof msg.text === 'string' && msg.text) {
            partialRef.current += msg.text;
            emitTranscript();
          }
          break;
        case 'final':
          commitFinalSegment(msg.text);
          break;
        case 'error':
          fail(
            'connection',
            typeof msg.error === 'string' ? msg.error : 'Transcription failed.'
          );
          break;
        default:
          break;
      }
    },
    [handleReady, commitFinalSegment, emitTranscript, fail]
  );

  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'stop' }));
      } catch {
        /* socket already gone */
      }
    }
    teardown();
    if (isMountedRef.current) {
      setIsRecording(false);
      setIsConnecting(false);
    }
  }, [teardown]);

  // Capture the mic and wire up the resampling worklet for an open socket.
  const connectAudio = useCallback(
    async (ws: WebSocket) => {
      const AudioCtor = getAudioContextCtor();
      if (!AudioCtor) {
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Aborted (stopped / unmounted) while awaiting permission.
      if (!isMountedRef.current || wsRef.current !== ws) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;

      const context = new AudioCtor();
      audioContextRef.current = context;

      await context.audioWorklet.addModule(WORKLET_MODULE_URL);
      if (!isMountedRef.current || wsRef.current !== ws) {
        return;
      }

      const source = context.createMediaStreamSource(stream);
      sourceRef.current = source;
      const worklet = new AudioWorkletNode(context, PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { targetSampleRate: TARGET_SAMPLE_RATE },
      });
      workletNodeRef.current = worklet;
      worklet.port.onmessage = (event) => {
        const socket = wsRef.current;
        if (
          readyRef.current &&
          socket?.readyState === WebSocket.OPEN &&
          event.data instanceof ArrayBuffer
        ) {
          socket.send(event.data);
        }
      };

      // Keep the graph live without audible feedback: worklet → 0-gain → output.
      const sink = context.createGain();
      sink.gain.value = 0;
      source.connect(worklet);
      worklet.connect(sink);
      sink.connect(context.destination);

      if (context.state === 'suspended') {
        await context.resume();
      }
      // Audio is now flowing — reflect "recording" once the server is also ready.
      audioLiveRef.current = true;
      markRecordingIfReady();
    },
    [markRecordingIfReady]
  );

  const start = useCallback(async () => {
    // Ignore overlapping starts.
    if (wsRef.current || isConnecting || isRecording) {
      return;
    }
    const envError = getEnvironmentError();
    if (envError) {
      onErrorRef.current?.(envError.kind, envError.message);
      return;
    }
    // Require an authenticated session — don't connect with an empty token.
    const { accessToken } = retrieveAuthTokens();
    if (!accessToken) {
      onErrorRef.current?.('connection', 'Please sign in to use voice input.');
      return;
    }

    committedRef.current = '';
    partialRef.current = '';
    readyRef.current = false;
    setIsConnecting(true);

    // 1) Open the socket. The JWT is carried in the second WS subprotocol
    // (`bearer.<jwt>`) rather than a query param so it never lands in proxy/
    // access logs or browser history. JWT chars are subprotocol-safe, so the
    // token is sent as-is. Route via /api/chat-agent (same as the chat stream)
    // so the dev proxy forwards to the Ask Moo backend, rewriting to
    // /api/v1/transcribe/stream; /api/v1/... directly would hit the catch-all.
    const { protocol, host } = globalThis.location;
    const scheme = protocol === 'https:' ? 'wss' : 'ws';
    const url = `${scheme}://${host}/api/chat-agent/v1/transcribe/stream`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url, [WS_APP_PROTOCOL, `bearer.${accessToken}`]);
    } catch {
      fail('connection', 'Could not connect to the transcription service.');
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        handleMessage(event.data);
      }
    };
    ws.onerror = () =>
      fail('connection', 'Voice connection failed. Please try again.');
    ws.onclose = (event) => {
      if (wsRef.current !== ws) {
        return;
      }
      // Auth rejection — the server closes cleanly with 4401, so check it before
      // the clean-close branch. Prompt re-auth rather than retrying blindly.
      if (event.code === 4401) {
        fail('permission', 'Your session has expired. Please sign in again.');
        return;
      }
      // A clean close (e.g. the server ending the session after the final
      // segment) is a normal stop, not an error — tear down silently.
      if (event.wasClean || event.code === 1000) {
        teardown();
        if (isMountedRef.current) {
          setIsRecording(false);
          setIsConnecting(false);
        }
        return;
      }
      fail('connection', 'Voice connection closed unexpectedly.');
    };
    // Surface a hung connection instead of a spinner that never resolves.
    readyTimerRef.current = setTimeout(() => {
      if (wsRef.current === ws && !readyRef.current) {
        fail(
          'connection',
          'The transcription service did not respond. Please try again.'
        );
      }
    }, 12000);

    // 2) Capture the mic and stream it.
    try {
      await connectAudio(ws);
    } catch (err) {
      const denied =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'SecurityError');
      fail(
        denied ? 'permission' : 'unsupported',
        denied
          ? 'Please allow microphone access to use voice input.'
          : 'Could not start microphone capture.'
      );
    }
  }, [fail, teardown, handleMessage, connectAudio, isConnecting, isRecording]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      teardown();
    };
  }, [teardown]);

  return {
    isActive: isConnecting || isRecording,
    isRecording,
    start,
    stop,
  };
}

export default useLiveTranscription;
